// Copyright (c) 2022 by Move Closer

import { AnyObject, Headers, Injectable, IResponse } from '@movecloser/front-core'
import fetch from 'cross-fetch'
import {
  ApolloClient,
  ApolloQueryResult,
  FetchResult,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject
} from '@apollo/client'
import { Response } from '@movecloser/front-core/lib/services/http/response'

import { GraphQLConfig, IGraphQLDriver, MutationOptions, QueryOptions } from './driver.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export class GraphQlDriver implements IGraphQLDriver {
  protected client: ApolloClient<NormalizedCacheObject>
  protected debug: boolean

  constructor (config: GraphQLConfig, debug: boolean = false) {
    this.debug = debug

    try {
      const httpLink: HttpLink = GraphQlDriver.createHttpLinkFromConfig(config)

      // Init instance of Apollo Client.
      this.client = new ApolloClient({
        link: httpLink,
        cache: new InMemoryCache()
      })
    } catch (e: any) {
      throw new Error(e)
    }
  }

  /**
   * @inheritDoc
   */
  public async mutate (
    mutation: MutationOptions,
    headers: Headers
  ): Promise<IResponse> {
    const result: FetchResult = await this.client.mutate({
      ...mutation,
      context: { headers },
      errorPolicy: 'all'
    })

    const response = new Response(
      this.resolveStatus(result),
      result.data || {},
      {},
      result.errors
    )

    if (this.debug) {
      this.logResponse(mutation, headers, response)
    }

    return response
  }

  /**
   * @inheritDoc
   */
  public async query (
    query: QueryOptions,
    headers: Headers
  ): Promise<IResponse> {
    const result: ApolloQueryResult<AnyObject> = await this.client.query({
      fetchPolicy: 'no-cache',
      ...query,
      context: { headers },
      errorPolicy: 'all'
    })

    const response = new Response(
      this.resolveStatus(result),
      result.data || {},
      {},
      result.errors
    )

    if (this.debug) {
      this.logResponse(query, headers, response)
    }

    return response
  }

  protected logResponse (input: QueryOptions | MutationOptions, headers: Headers, response: IResponse): void {
    console.log('GraphQL:', { input, headers, response })
  }

  protected resolveStatus (response: FetchResult<AnyObject>): number {
    if (!Array.isArray(response.errors) || response.errors.length === 0) {
      return 200
    }

    switch (response.errors[0].extensions.category) {
      case 'graphql-authorization':
        return 401
      default:
        return 500
    }
  }

  private static createHttpLinkFromConfig (config: GraphQLConfig): HttpLink {
    if (!Object.keys(config).length) {
      throw new Error('[GraphQlDriver]: Config is empty!')
    }

    return new HttpLink({
      uri: config.baseURL,
      headers: config.headers,
      fetch
    })
  }
}
